import {ScopeChainNode} from "./analysis";
import {cafebabeUnreachable} from "./error";
import {
	CatchStatementTree,
	ClassDeclarationTree,
	EnhancedForLoopTree,
	EnumDeclarationTree,
	FunctionArgumentsTree,
	FunctionArgumentTree,
	FunctionDeclarationTree,
	IdentTree,
	InterfaceDeclarationTree,
	LocalVariableDeclarationListTree,
	LocalVariableDeclarationTree,
} from "./model";
import {docCommentToHtml} from "./doc_comment";
import {ConstevalValue} from "./constant_evaluation";

/**
 * Base data common to all non-package declarations (functions, classes).
 */
export type DeclarationInfoBase = {
	// File in which this declaration was found
	file: string;
	// Whether this declaration is considered a builtin
	builtin: boolean;
	// Accessor flags, e.g. static, final
	flags: AccessorFlags;
};

/**
 * Bitset flags common to all declarations (although not all will necessarily be used).
 */
export enum AccessorFlags {
	public = 1,
	private = 2,
	protected = 4,
	static = 8,
	final = 16,
	abstract = 32,
	strictfp = 64,
	default = 128,
}

// Test whether the given declaration has a flag
export function hasFlag(
	a: {flags: AccessorFlags} | null,
	flag: AccessorFlags
): boolean {
	return !!a && (a.flags & flag) === flag;
}

export type MethodDeclarationInfo = DeclarationInfoBase & {
	type: "function";

	/**
	 * e.g. T in <T> T foo()
	 */
	typeParameters: TypeParameterInfo[];

	/**
	 * e.g. int  in int foo()
	 */
	returns: GeneralType;

	/**
	 * List of function arguments
	 */
	arguments: GeneralType[];

	/**
	 * List of function argument names
	 */
	names: string[];

	/**
	 * e.g. foo in int foo()
	 */
	name: string;

	/**
	 * Enclosing class or interface
	 */
	memberOf: ClassDeclarationInfo | InterfaceDeclarationInfo;

	/**
	 * List of local or anonymous classes
	 */
	innerClasses: (ClassDeclarationInfo | InterfaceDeclarationInfo)[];

	/**
	 * Whether the method is a constructor
	 */
	isCtor: boolean;

	/**
	 * Whether the last argument to the method is a variable arity declaration, e.g. Object... a
	 */
	isVariableArity: boolean;

	/**
	 * List of types that this function may throw.
	 */
	throws: NonGenericType[];

	/**
	 * Doc string
	 */
	doc?: string;

	tree?: FunctionDeclarationTree | null;
};

/**
 * Base property-set for interfaces and classes.
 */
export type InterfaceOrClassMixin = {
	// See https://docs.oracle.com/javase/specs/jls/se8/html/jls-13.html
	// e.g. java.lang.Object, AopsTheater.AopsTheater java.util.Map$Entry (i.e., <enclosedBy.binaryName> '$' <simpleName>)
	// Binary names are used during resolution from the input JSON file
	binaryName: string;
	// Package containing the interface, e.g. AopsTheater
	package: PackageInfo | null;
	// List of type parameters
	typeParameters: TypeParameterInfo[];
	// e.g. Object, AopsTheater
	simpleName: string;
	// Whether this class is a top-level class (i.e., not nested within another class)
	isTopLevel: boolean;
	// Whether this class is an inner class (i.e., it's not static, so it has access to an instance of the enclosing class)
	isInner: boolean;
	// This class is a nested class within this class, e.g. Map.Entry is enclosed by Map
	enclosedBy: ClassDeclarationInfo | InterfaceDeclarationInfo | null;
	// This class is an inner class of this class and therefore has access to members/types of the outer class
	innerOf: ClassDeclarationInfo | InterfaceDeclarationInfo | null;
	// All nested classes
	memberClasses: Map<string, ClassDeclarationInfo | InterfaceDeclarationInfo>;
	// List of functions
	functions: Map<string, MethodDeclarationInfo[] /* overloads */>;
	// List of members
	members: Map<string, MemberFieldInfo>;
	// Doc string
	doc?: string;
};

export type InterfaceDeclarationInfo = DeclarationInfoBase &
	InterfaceOrClassMixin & {
		type: "interface";
		tree: InterfaceDeclarationTree | null;
		extends: PermittedSuperclassType[];
	};

export type MemberFieldInfo = DeclarationInfoBase & {
	type: "member";
	name: string;
	memberOf:
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo
		| "array" /* for .length only */;
	ty: GeneralTypeOrAny;
	constantValue?: string | null;
	constevalValue?: ConstevalValue;
	doc?: string;
};

/**
 * Information associated with a package.
 *
 * Example entry:
 *
 * {
 *     type: "package",
 *     name: "AopsTheater",
 *     parent: null,
 *     classes: [ ... ]
 * }
 */
export type PackageInfo = {
	type: "package";
	name: string;
	fullyQualifiedName: string;
	builtin: boolean;
	parent: PackageInfo | null;
	subpackages: Map<string, PackageInfo>;
	topLevelClasses: Map<string, ClassDeclarationInfo | InterfaceDeclarationInfo>;
	description: string;
	scope: ScopeChainNode | null;
};

/**
 * Type information associated with a type parameter.
 *
 * Example entry for S in the following:
 * class Cow <S extends T & Pox, T extends S>
 *
 * {
 *     type: "tparam",
 *     name: "S",
 *     parameterOf: { type: "class", name: "Cow", ... },
 *     extends: { type: "tparam", name: "T", ... },
 *     extendsInterfaces: [ { type: "interface", name: "Pox", ... } ]
 * }
 */
export type TypeParameterInfo = {
	type: "tparam";

	// e.g. A in A extends B
	name: string;
	parameterOf:
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo
		| MethodDeclarationInfo;
	// Intersection types are folded into here, e.g. in A extends B & C
	extends: ReferenceType | null;
};

/**
 * Class (non-interface) declaration
 */
export type ClassDeclarationInfo = DeclarationInfoBase &
	InterfaceOrClassMixin & {
		type: "class";

		// List of superinterfaces
		implements: PermittedSuperclassType[];
		// Only java.lang.Object does not have a superclass
		extends: PermittedSuperclassType | null;

		// enums extend java.lang.Enum
		isEnum: boolean;

		tree: ClassDeclarationTree | EnumDeclarationTree | null;
	};

export type TypeDeclarationInfo =
	| ClassDeclarationInfo
	| InterfaceDeclarationInfo;

/**
 * Type which may be the superclass of another class.
 */
export type PermittedSuperclassType =
	| RawType
	| NonGenericType
	| ParameterizedType;
export type ReferenceType =
	| IntersectionType
	| RawType
	| NonGenericType
	| TypeVariableType
	| ParameterizedType
	| ArrayType;

export type NullType = {
	type: "null";
};

export type PrimitiveType = {
	type: "primitive";
	primitive:
		| "boolean"
		| "byte"
		| "char"
		| "short"
		| "int"
		| "float"
		| "long"
		| "double"
		| "void";
	boxed: string;
};

export type IntersectionType = {
	type: "intersection";
	components: ReferenceType[];
};

/**
 * Type of a class which has type arguments but which was instantiated without type arguments, e.g. the type
 * of the expression new ArrayList()
 */
export type RawType = {
	type: "raw";
	rawOf: ClassDeclarationInfo | InterfaceDeclarationInfo;
};

/**
 * Type of a class which has no type arguments.
 */
export type NonGenericType = {
	type: "nongeneric";
	ty: ClassDeclarationInfo | InterfaceDeclarationInfo;
};

/**
 * Type variable with an optional association with an actual type parameter, and upper and lower bounds.
 */
export type TypeVariableType = {
	type: "typevar";
	// associated type parameter, if it exists
	typeParameter: TypeParameterInfo | null;
	upperBound: ReferenceType | null;
	lowerBound: ReferenceType | null;
};

export type WildcardType = {
	type: "wildcard";
	boundKind: "super" | "extends" | "none";
	bound: ReferenceType | null /* null = no bound, i.e. extends Object */;
};

/**
 * Example 1:
 * HashMap<K, V>
 *    parameterizes: [ HashMap ]
 *    typeArguments: [ TypeVariable K, TypeVariable V ]
 *
 * Example 2:
 * Seq<String>.Zipper<Integer>
 *    parameterizes: [ Zipper ]
 *    typeArguments: [ String, Integer ]  (includes parent arguments, since no "mixed" raw/parameterized types)
 */
export type ParameterizedType = {
	type: "parameterized";
	parameterizes: ClassDeclarationInfo | InterfaceDeclarationInfo;
	typeArguments: (ReferenceType | WildcardType)[];
};

/**
 * Type of an array, e.g. int[] or String[]
 */
export type ArrayType = {
	type: "array";
	arrayOf: PrimitiveType | ReferenceType;
};

/**
 * Type of an expression which we gave up type inference on -- should be propagated
 */
export type AnyType = {
	type: "any";
};

export type GeneralType = NullType | PrimitiveType | ReferenceType;
export type GeneralTypeOrAny = GeneralType | AnyType;

export type PrettyPrintTypeOptions = {};

export function prettyPrintType(
	ty: GeneralTypeOrAny | WildcardType | null,
	options?: PrettyPrintTypeOptions
): string {
	if (!ty) return "";
	switch (ty.type) {
		case "any":
			return "";
		case "null":
			return "<null>";
		case "primitive":
			return ty.primitive;
		case "intersection":
			return ty.components.map((t) => prettyPrintType(t, options)).join(" & ");
		case "raw":
			return ty.rawOf.simpleName;
		case "nongeneric":
			return ty.ty.simpleName;
		case "typevar":
			return ty.typeParameter?.name ?? "<unnamed type parameter w/ bounds"; // TODO
		case "parameterized": {
			const tyArgs = ty.typeArguments.map((arg) =>
				prettyPrintType(arg, options)
			);
			let curr: ClassDeclarationInfo | InterfaceDeclarationInfo | null =
				ty.parameterizes;
			let result = "";
			let addDot = false;
			while (curr !== null) {
				const args = tyArgs.splice(-curr.typeParameters.length);
				result =
					curr.simpleName +
					(args.length ? "<" + args.join(", ") + ">" : "") +
					(addDot ? "." : "") +
					result;
				addDot = true;
				curr = curr.enclosedBy;
			}
			return result;
		}
		case "array":
			return prettyPrintType(ty.arrayOf) + "[]";
		case "wildcard": // ? extends Cow, ? super Egg
			return (
				"?" +
				(ty.boundKind !== "none"
					? ` ${ty.boundKind} ${prettyPrintType(ty.bound!)}`
					: "")
			);
	}
}

export type PrettyPrintOptions = {
	capitalize?: boolean;
	doc?: boolean;
	fullyQualified?: boolean;
	scope?: ScopeChainNode;
};

export function prettyPrintInfo(
	info:
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo
		| PackageInfo
		| TypeParameterInfo
		| MethodDeclarationInfo
		| MemberFieldInfo
		| LocalVariableDef
		| "array",
	options: PrettyPrintOptions = {}
): string {
	if (info === "array") return "array";
	function cap(s: string) {
		const result =
			options?.capitalize && s ? s[0]!.toUpperCase() + s.slice(1) : s;
		if (typeof info === "object" && "doc" in info && options?.doc) {
			return result + (info.doc ? "<br><br>" + docCommentToHtml(info.doc) : "");
		}
		return result;
	}
	const fullyQualified = !!options?.fullyQualified;
	switch (info.type) {
		case "class":
			return cap(
				`class <code>${fullyQualified ? info.binaryName : info.simpleName}</code>`
			);
		case "interface":
			return cap(
				`interface <code>${fullyQualified ? info.binaryName : info.simpleName}</code>`
			);
		case "package":
			return cap(`package <code>${info.fullyQualifiedName}</code>`);
		case "tparam":
			return cap(`type parameter <code>${info.name}</code>`);
		case "function":
			return cap(
				`function <code>${info.name}</code> on ${prettyPrintInfo(info.memberOf, options)}`
			);
		case "member":
			return cap(`member <code>${info.name}</code>`);
		case "local":
			return cap(`local variable <code>${info.name}</code>`);
	}
}

/**
 * Attempt to remove parameters from the given type and return the underlying class or interface declaration info.
 * @param ty The raw, parameterized or non-generic type to remove parameters from.
 */
export function unparameterize(
	ty: RawType | ParameterizedType | NonGenericType
): ClassDeclarationInfo | InterfaceDeclarationInfo {
	switch (ty.type) {
		case "raw":
			return ty.rawOf;
		case "parameterized":
			return ty.parameterizes;
		case "nongeneric":
			return ty.ty;
	}
	cafebabeUnreachable("unparameterize");
}

/**
 * Attempt to remove parameters from the given type and return the underlying class or interface declaration info,
 * or null if this action is not applicable to the given type.
 * @param ty The raw, parameterized or non-generic type to remove parameters from.
 */
export function maybeUnparameterize(
	ty: GeneralTypeOrAny | WildcardType | null | undefined
): ClassDeclarationInfo | InterfaceDeclarationInfo | null {
	if (!ty) return null;
	switch (ty.type) {
		case "raw":
		case "parameterized":
		case "nongeneric":
			return unparameterize(ty);
	}
	return null;
}

export type LocalVariableDef = {
	type: "local";
	name: string;
	def: LocalVariableDeclarationTree | FunctionArgumentTree | IdentTree;
	parent:
		| LocalVariableDeclarationListTree
		| FunctionArgumentsTree
		| EnhancedForLoopTree
		| CatchStatementTree;
	ty: GeneralTypeOrAny;
	memberOf:
		| MethodDeclarationInfo
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo
		| null;
	variableArity: boolean;
	constantValue: string | null;
};

/**
 * Label in the Java source code, e.g. for break and continue statements.
 */
export type LabelInfo = {
	type: "label";
	label: string;
	scope: ScopeChainNode;
	precedesLoop: boolean;
};

export type Ambiguous<T> = {type: "ambiguous"; resolutions: T[]};
export type PossiblyAmbiguous<T> = T | Ambiguous<T>;

/**
 * Merge ambiguous declarations, methods, etc. in set A with set B.
 */
export function mergeAmbiguous<T extends {type: string}>(
	a: PossiblyAmbiguous<T> | null,
	b: PossiblyAmbiguous<T> | null
): PossiblyAmbiguous<T> | null {
	if (a === null || a === b) return b;
	if (b === null) return a;
	if (b.type === "ambiguous") [a, b] = [b, a];
	if (a.type === "ambiguous") {
		const res = (a as Ambiguous<T>).resolutions;
		if (b.type === "ambiguous") res.push(...(b as Ambiguous<T>).resolutions);
		else res.push(b as T);
	} else {
		return {type: "ambiguous", resolutions: [a as T, b as T]};
	}
	return a;
}
